import { supabase } from '../../../lib/supabaseClient';

export async function getTemplates() {
  const { data, error } = await supabase
    .from('templates')
    .select(`
      id,
      name,
      slug,
      description,
      category,
      current_version_id,
      created_at,
      updated_at
    `)
    .order('category')
    .order('name');

  if (error) throw error;
  return data;
}

export async function getTemplateBySlug(slug) {
  const { data, error } = await supabase
    .from('templates')
    .select(`
      id,
      name,
      slug,
      description,
      category,
      current_version_id,
      created_at,
      updated_at
    `)
    .eq('slug', slug)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getTemplateVersions(templateId) {
  const { data, error } = await supabase
    .from('template_versions')
    .select(`
      id,
      template_id,
      version_number,
      content,
      variables,
      change_notes,
      status,
      created_at,
      created_by,
      published_at
    `)
    .eq('template_id', templateId)
    .order('version_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getTemplateVersion(versionId) {
  const { data, error } = await supabase
    .from('template_versions')
    .select('*')
    .eq('id', versionId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function createTemplate({ name, slug, description, category }) {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('templates')
    .insert({
      name,
      slug,
      description,
      category,
      created_by: user?.id
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateTemplate(templateId, { name, description, category }) {
  const { data, error } = await supabase
    .from('templates')
    .update({
      name,
      description,
      category,
      updated_at: new Date().toISOString()
    })
    .eq('id', templateId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createTemplateVersion(templateId, { content, variables, changeNotes }) {
  const { data: { user } } = await supabase.auth.getUser();

  const { data: nextVersion, error: versionError } = await supabase
    .rpc('get_next_template_version', { p_template_id: templateId });

  if (versionError) throw versionError;

  const { data, error } = await supabase
    .from('template_versions')
    .insert({
      template_id: templateId,
      version_number: nextVersion,
      content,
      variables: variables || [],
      change_notes: changeNotes,
      status: 'draft',
      created_by: user?.id
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateDraftVersion(versionId, { content, variables, changeNotes }) {
  const { data, error } = await supabase
    .from('template_versions')
    .update({
      content,
      variables: variables || [],
      change_notes: changeNotes
    })
    .eq('id', versionId)
    .eq('status', 'draft')
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function publishTemplateVersion(versionId) {
  const { data, error } = await supabase
    .rpc('publish_template_version', { p_version_id: versionId });

  if (error) throw error;
  return data;
}

export async function getPublishedTemplate(slug) {
  const { data, error } = await supabase
    .rpc('get_published_template', { p_slug: slug });

  if (error) throw error;
  return data?.[0] || null;
}

export function extractVariables(content) {
  const regex = /\[\[([a-z_]+)\]\]/g;
  const variables = new Set();
  let match;

  while ((match = regex.exec(content)) !== null) {
    variables.add(match[1]);
  }

  return Array.from(variables).map(name => ({
    name,
    description: ''
  }));
}
